// Manutenzione attrezzature — MOD 06 (T-251). Scheda di registro.
// Sorgente: public.equipment_maintenance (stessa tabella dell'export MOD 06).

import { confirmDialog } from '../../lib/ui/dialogs.js';
import { toUserMessage } from '../../lib/errors.js';
import {
  IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonInput,
  IonItem, IonLabel, IonModal, IonNote, IonPage, IonSelect, IonSelectOption,
  IonSpinner, IonTextarea, IonTitle, IonToast, IonToggle, IonToolbar,
} from '@ionic/react';
import { addOutline, warningOutline } from 'ionicons/icons';
import { useMemo, useState } from 'react';

import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useOfflineRegister, offlineRegisterStatus } from '../../lib/haccp/useOfflineRegister.js';

interface MaintRow {
  id: string;
  data_intervento: string;
  attrezzatura: string;
  categoria_attrezzatura: string | null;
  tipo_intervento: string;
  esecutore_interno: boolean;
  esecutore_nome: string | null;
  esecutore_ditta: string | null;
  descrizione: string;
  esito: string;
  prossima_scadenza: string | null;
}

const CATEGORIA: Array<[string, string]> = [
  ['cottura', 'Cottura'], ['refrigerazione', 'Refrigerazione'], ['lavaggio', 'Lavaggio'],
  ['preparazione', 'Preparazione'], ['aspirazione', 'Aspirazione'], ['bar', 'Bar'],
  ['colazione', 'Colazione'], ['trasporto', 'Trasporto'], ['altro', 'Altro'],
];
const TIPO: Array<[string, string]> = [
  ['manutenzione_ordinaria', 'Manutenzione ordinaria'], ['riparazione', 'Riparazione'],
  ['sostituzione_pezzo', 'Sostituzione pezzo'], ['sostituzione_completa', 'Sostituzione completa'],
  ['taratura', 'Taratura'], ['verifica_funzionale', 'Verifica funzionale'],
  ['pulizia_profonda', 'Pulizia profonda'], ['sanificazione_straordinaria', 'Sanificazione straordinaria'], ['altro', 'Altro'],
];
const ESITO: Array<[string, string]> = [
  ['ok', 'OK'], ['ok_con_riserva', 'OK con riserva'],
  ['ko_attrezzatura_fuori_servizio', 'KO fuori servizio'], ['rinviato', 'Rinviato'],
];

function todayIso(): string { return new Date().toISOString().slice(0, 10); }
function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return y && m && dd ? `${dd}/${m}/${y}` : String(d);
}
function labelOf(o: Array<[string, string]>, v: string): string { return o.find(([k]) => k === v)?.[1] ?? v; }
function statoScad(scad: string | null): { label: string; color: string } | null {
  if (!scad) return null;
  const days = Math.floor((new Date(scad + 'T00:00:00Z').getTime() - Date.now()) / 86400000);
  if (days < 0) return { label: 'Scaduto', color: '#dc2626' };
  if (days <= 30) return { label: `${days}gg`, color: '#ea7300' };
  return { label: 'OK', color: '#16a34a' };
}

const EMPTY = {
  data_intervento: todayIso(), attrezzatura: '', categoria_attrezzatura: 'cottura',
  tipo_intervento: 'manutenzione_ordinaria', esecutore_interno: true, esecutore_nome: '',
  esecutore_ditta: '', descrizione: '', esito: 'ok', prossima_scadenza: '',
};

export function ManutenzionePage(): JSX.Element {
  const { profile } = useAuth();
  const canEdit = profile?.is_platform_owner === true || profile?.role === 'consulente' || profile?.role === 'ristoratore';
  const [toast, setToast] = useState<string | null>(null);

  const { register, form, setForm, open, start, close, save: saveRecord, saving, ready,
    hasDraft, hasDraftChanges, discard: discardRecord, draftStatus, draftError, retryDraft } = useOfflineRegister<MaintRow, typeof EMPTY>(
    'equipment_maintenance', EMPTY, 'data_intervento', canEdit,
  );
  const { data: rows = [], isLoading: loading, refetch: load } = register;

  const expiring = useMemo(() => rows.filter((r) => { const s = statoScad(r.prossima_scadenza); return s && (s.label === 'Scaduto' || s.color === '#ea7300'); }).length, [rows]);

  async function discard(): Promise<void> {
    const remove = async () => {
      try { await discardRecord(); }
      catch (error) { setToast('Impossibile scartare la bozza: ' + toUserMessage(error)); }
    };
    if (!hasDraftChanges) { await remove(); return; }
    if (await confirmDialog('I dati della bozza saranno eliminati da questo dispositivo.', {
      header: 'Scartare la bozza?', okText: 'Scarta bozza', danger: true,
    })) await remove();
  }

  async function save(): Promise<void> {
    if (!profile?.active_tenant_id) { setToast('Nessun ristorante attivo'); return; }
    if (!form.attrezzatura.trim()) { setToast("Indica l'attrezzatura"); return; }
    if (form.descrizione.trim().length < 5) { setToast('Descrizione troppo breve (min 5 caratteri)'); return; }
    try {
      const saved = await saveRecord({
        data_intervento: form.data_intervento,
        attrezzatura: form.attrezzatura.trim(),
        categoria_attrezzatura: form.categoria_attrezzatura,
        tipo_intervento: form.tipo_intervento,
        esecutore_interno: form.esecutore_interno,
        esecutore_nome: form.esecutore_nome.trim() || null,
        esecutore_ditta: form.esecutore_ditta.trim() || null,
        descrizione: form.descrizione.trim(),
        esito: form.esito,
        prossima_scadenza: form.prossima_scadenza || null,
      });
      if (saved) setToast('Salvato sul dispositivo. In attesa di sincronizzazione.');
    } catch (error) {
      setToast('Salvataggio non riuscito: ' + toUserMessage(error));
    }
  }

  return (
    <IonPage>
      <IonHeader><IonToolbar>
        <IonButtons slot="start"><IonBackButton defaultHref="/haccp/registri-compila" /></IonButtons>
        <IonTitle>Manutenzione attrezzature</IonTitle>
      </IonToolbar></IonHeader>
      <IonContent className="ion-padding">
        <p role="status" style={{ color: '#64748b', fontSize: 13 }}>
          Le registrazioni si salvano sul dispositivo, anche senza rete, e si sincronizzano quando la connessione è disponibile.
        </p>
        {draftError && !open && <div role="alert"><p>{draftError}</p><IonButton onClick={retryDraft}>Riprova bozza</IonButton></div>}
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
          Registro MOD 06: interventi su attrezzature di cucina/bar + scadenze. L'export A4 è in Export registri.
        </p>
        {expiring > 0 && (
          <IonItem color="warning" lines="none" style={{ borderRadius: 10, marginBottom: 8 }}>
            <IonIcon icon={warningOutline} slot="start" />
            <IonLabel className="ion-text-wrap" style={{ fontSize: 13 }}>{expiring} {expiring === 1 ? 'intervento in scadenza/scaduto' : 'interventi in scadenza/scaduti'}.</IonLabel>
          </IonItem>
        )}
        {canEdit && (
          <IonButton expand="block" disabled={!ready || saving} onClick={start} style={{ '--border-radius': '12px', marginBottom: 12 } as React.CSSProperties}>
            <IonIcon icon={addOutline} slot="start" /> {hasDraft ? 'Riprendi bozza' : 'Registra intervento'}
          </IonButton>
        )}
        {register.isError && rows.length > 0 && (
          <div role="status"><p>Lettura non riuscita: sono mostrati gli ultimi dati caricati.</p><IonButton onClick={() => { void load(); }}>Riprova</IonButton></div>
        )}
        {loading ? <div style={{ textAlign: 'center', padding: 24 }}><IonSpinner /></div>
          : (register.isPending || register.isError) && rows.length === 0 ? (
          <div role="status" style={{ padding: 16 }}>
            <p>Impossibile leggere il registro sul dispositivo. Riprova.</p>
            <IonButton onClick={() => { void load(); }}>Riprova</IonButton>
          </div>
        ) : rows.length === 0 ? <p style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>Nessun intervento disponibile su questo dispositivo.</p>
          : rows.map((r) => {
            const st = statoScad(r.prossima_scadenza);
            const esecutore = r.esecutore_interno ? `Interno${r.esecutore_nome ? ' - ' + r.esecutore_nome : ''}` : [r.esecutore_ditta, r.esecutore_nome].filter(Boolean).join(' - ') || 'Ditta esterna';
            return (
              <div key={r.id} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 8, boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>
                {offlineRegisterStatus(r) && <p role="status" style={{ fontSize: 12, color: r._syncStatus === 'rejected' ? '#b91c1c' : '#92400e', margin: '0 0 6px' }}>{offlineRegisterStatus(r)}</p>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <b style={{ fontSize: 14 }}>{r.attrezzatura}</b>
                  <span style={{ fontSize: 11, color: '#64748b' }}>{fmtDate(r.data_intervento)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#334155', marginTop: 4 }}>{labelOf(TIPO, r.tipo_intervento)} · {labelOf(ESITO, r.esito)} · {esecutore}</div>
                <div style={{ fontSize: 13, color: '#0f172a', marginTop: 2 }}>{r.descrizione}</div>
                {st && <div style={{ fontSize: 12, marginTop: 4 }}>Prossima scadenza: <b>{fmtDate(r.prossima_scadenza)}</b> <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 700, color: '#fff', background: st.color, padding: '1px 7px', borderRadius: 999 }}>{st.label}</span></div>}
              </div>
            );
          })}

        <IonModal isOpen={open} canDismiss={!saving} onDidDismiss={close}>
          <IonHeader><IonToolbar>
            <IonTitle>Nuovo intervento</IonTitle>
            <IonButtons slot="end">
              <IonButton color="danger" disabled={saving || !ready} onClick={() => { void discard(); }}>Scarta bozza</IonButton>
              <IonButton disabled={saving} onClick={close}>Chiudi</IonButton>
            </IonButtons>
          </IonToolbar></IonHeader>
          <IonContent className="ion-padding">
            <p role="status" style={{ color: '#64748b', fontSize: 13 }}>
              {draftStatus}
            </p>
            {draftError && <div role="alert"><p>{draftError}</p><IonButton disabled={saving} onClick={retryDraft}>Riprova bozza</IonButton></div>}
            <IonItem><IonInput type="date" label="Data intervento *" labelPlacement="stacked" value={form.data_intervento} onIonInput={(e) => setForm((f) => ({ ...f, data_intervento: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonInput label="Attrezzatura *" labelPlacement="stacked" placeholder="Es. Forno combinato Rational" value={form.attrezzatura} onIonInput={(e) => setForm((f) => ({ ...f, attrezzatura: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonSelect label="Categoria" labelPlacement="stacked" value={form.categoria_attrezzatura} onIonChange={(e) => setForm((f) => ({ ...f, categoria_attrezzatura: String(e.detail.value) }))}>{CATEGORIA.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}</IonSelect></IonItem>
            <IonItem><IonSelect label="Tipo intervento *" labelPlacement="stacked" value={form.tipo_intervento} onIonChange={(e) => setForm((f) => ({ ...f, tipo_intervento: String(e.detail.value) }))}>{TIPO.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}</IonSelect></IonItem>
            <IonItem><IonLabel>Eseguito internamente</IonLabel><IonToggle checked={form.esecutore_interno} onIonChange={(e) => setForm((f) => ({ ...f, esecutore_interno: e.detail.checked }))} /></IonItem>
            {!form.esecutore_interno && <IonItem><IonInput label="Ditta esterna" labelPlacement="stacked" value={form.esecutore_ditta} onIonInput={(e) => setForm((f) => ({ ...f, esecutore_ditta: String(e.detail.value ?? '') }))} /></IonItem>}
            <IonItem><IonInput label={form.esecutore_interno ? 'Eseguito da' : 'Tecnico'} labelPlacement="stacked" value={form.esecutore_nome} onIonInput={(e) => setForm((f) => ({ ...f, esecutore_nome: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonTextarea label="Descrizione *" labelPlacement="stacked" autoGrow value={form.descrizione} onIonInput={(e) => setForm((f) => ({ ...f, descrizione: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonSelect label="Esito *" labelPlacement="stacked" value={form.esito} onIonChange={(e) => setForm((f) => ({ ...f, esito: String(e.detail.value) }))}>{ESITO.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}</IonSelect></IonItem>
            <IonItem><IonInput type="date" label="Prossima scadenza" labelPlacement="stacked" value={form.prossima_scadenza} onIonInput={(e) => setForm((f) => ({ ...f, prossima_scadenza: String(e.detail.value ?? '') }))} /><IonNote slot="helper">Se l'intervento è periodico.</IonNote></IonItem>
            <IonButton expand="block" disabled={saving || !ready} onClick={() => { void save(); }} style={{ marginTop: 16, '--border-radius': '12px' } as React.CSSProperties}>{saving ? <IonSpinner name="dots" /> : 'Salva'}</IonButton>
          </IonContent>
        </IonModal>
        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2600} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
